import React, {useRef} from 'react'
import {ButtonAction} from "../model/ButtonAction"
import {Icon, IconName} from "./Icon"
import {ItemAction} from '@dv5/domain'
import {useHoverMenu} from '../hooks/useHoverMenu'
import {useLongPress} from '../hooks/useLongPress'
import './ExtViewButton.less'

interface ExtViewButtonProps {
  actions: Array<ButtonAction>
  onView: (actionType: ItemAction) => void
  tooltipText?: string
  className?: string
}

export const ExtViewButton: React.FC<ExtViewButtonProps> = ({
  actions,
  onView,
  tooltipText = 'View',
  className = ''
}) => {
  const { showPopup, setShowPopup, onMouseEnter, onMouseLeave } = useHoverMenu()
  const buttonRef = useRef<HTMLButtonElement>(null)
  const popupRef = useRef<HTMLDivElement>(null)

  const { onPointerDown, onPointerUp, onPointerCancel, onPointerLeave, isLongPressTriggered } = useLongPress({
    onLongPress: () => setShowPopup(true)
  })

  const handleMouseDown = (event: React.MouseEvent<HTMLButtonElement>) => {
    // Right click - show popup menu
    if (event.button === 2) {
      event.preventDefault()
      event.stopPropagation()
      setShowPopup(true)
    }
  }

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()

    // Left click - call onView with 0, unless a long press already opened the popup
    if (event.button === 0 && actions.length > 0 && !isLongPressTriggered()) {
      onView(actions[0].action)
    }
  }

  const handleContextMenu = (event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
  }

  const handleActionClick = (event: React.MouseEvent, index: number) => {
    event.preventDefault()
    event.stopPropagation()
    setShowPopup(false)
    if (index < actions.length) {
      onView(actions[index].action)
    }
  }

  // Close popup when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(event.target as Node) && !(buttonRef.current?.contains(event.target as Node) ?? false)) {
        setShowPopup(false)
      }
    }

    if (showPopup) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showPopup])

  return (
    <div className={`ext-view-button-container ${className}`}>
            <button
        ref={buttonRef}
        className="ext-view-button"
        onMouseDown={handleMouseDown}
        onClick={handleClick}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        onPointerLeave={onPointerLeave}
        onContextMenu={handleContextMenu}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        aria-label="View"
        title={`${tooltipText} (right click or long press for menu)`}
      >
        <Icon
          name={actions.length === 0 ? IconName.View : IconName.ExtView}
          className="ext-view-icon"
          width="20"
          height="20"
        />
      </button>

      {showPopup && (
        <div ref={popupRef} className="ext-view-popup">
          {actions.map((action, index) => (
            <div
              key={index}
              className="ext-view-popup-item"
              onMouseDown={event => handleActionClick(event, index)}
            >
              {action.text}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
